import { BaseModel, BaseWithId } from '@app/shared/models/base';
import { Gender } from '@app/shared/models/gender';
import { Address } from '@app/shared/models/address';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { BaseWithIdList } from '@app/shared/models/list';
import { ActionListItem } from '@app/shared/components/action-list/action-list.component';
import { AlphabetFilterable } from '@app/shared/models/search-filter';
import { UserRole } from '@app/shared/models/user-role';
import { UserAssignment } from '@app/shared/models/user-assignment';
import { Role } from '@app/core/authentication/permission';

export enum ClientUserState {
    INITIAL = 'INITIAL',  	    // new: only created, no invitation sent
    INVITED = 'INVITED',  	    // new: created, invitation sent
    NOTINVITED = 'NOTINVITED',  // state between temporary and invited -> shows that user was created and at least 1 time a save was performed
    TEMPORARY = 'TEMPORARY',    // deleted after register period (eg. 12 hours), waiting for email-confirmation to get registered
    REGISTERED = 'REGISTERED',  // new/changed: Invitation accepted, Password set
    LICENSED = 'LICENSED'	    // permanent
}

export enum AuthenticationMethod {
    SMS = 'SMS',
    TOTP = 'TOTP',
    STANDARD = 'STANDARD'
}

export class ClientUser extends BaseWithId implements ActionListItem, AlphabetFilterable {
    public gender: Gender;
    public title: string;

    public firstname: string;
    public lastname: string;
    public email: string;
    public email_lower: string;
    public user_code: string;
    public qualifications: string;

    public inactive: boolean;
    public locked: boolean;
    public state: ClientUserState;
    public locale: string;

    public address: Address;

    public roles: UserRole[] | Role[];

    public member_structure: UserAssignment;

    public emailchange_tan: string;

    public authentication_method: AuthenticationMethod | null;

    public authentication_sms_verified: boolean;
    public authentication_sms_phonenumber: string;
    public authentication_totp_verified: boolean;
    public authentication_totp_secret: string;

    get isVerified(): boolean {
        return !!this.authentication_method && (
            (this.authentication_method == AuthenticationMethod.SMS && !!this.authentication_sms_verified) || 
            (this.authentication_method == AuthenticationMethod.TOTP && !!this.authentication_totp_verified)
        );
    }

    get actionListDisplayName(): string {
        return `${this.firstname ? this.firstname : ''} ${this.lastname ? this.lastname : ''}`;
    }

    get alphabetFilterOn(): string[] {
        return [
            this.firstname ? this.firstname : '',
            this.lastname ? this.lastname : '',
            this.user_code ? this.user_code : ''
        ];
    }

    public isVerifiedFor(method: AuthenticationMethod): boolean {
        return !!this.authentication_method && (
            (this.authentication_method == method && !!this.authentication_sms_verified) || 
            (this.authentication_method == method && !!this.authentication_totp_verified)
        ); 
    }

    public readonly trans = {
        gender: marker('client_user.gender'),
        title: marker('client_user.title'),
        firstname: marker('client_user.firstname'),
        lastname: marker('client_user.lastname'),
        email: marker('client_user.email'),
        user_code: marker('client_user.user_code'),
        qualifications: marker('client_user.qualifications'),
        roles: marker('client_user.roles')
    };

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            address: new Address(json && json.address),
            member_structure: json && json.member_structure ? new UserAssignment(json.member_structure) : null,
            roles: json && json.roles ? json.roles.map(role => new UserRole(role)) : [],
            firstname: json ? (json.firstname ? json.firstname : json.firstName) : null,
            lastname: json ? (json.lastname ? json.lastname : json.lastName) : null
        });
    }
}

export class ClientUserQueryResult extends BaseModel {
    public count_licences_in_use: number;
    public count_licences_overall: number;
    public count_users_active: number;
    public count_users_inactive: number;
    public count_users_initial: number;
    public count_users_locked: number;
    public count_users_not_invited: number;
    public count_users_overall: number;
    public count_users_pending: number;
    public count_users_mfa_activated: number;
    public count_users_mfa_deactivated: number;
    public count_users_mfa_verified: number;
    public count_users_mfa_not_verified: number;

    public users: BaseWithIdList<ClientUser>;

    constructor(json?: any) {
        super(json);
    }

    protected setObjectMappings(json: any): any {
        return Object.assign(super.setObjectMappings(json), {
            users: new BaseWithIdList(ClientUser, json && json.users)
        });
    }
}
